import { FC, useMemo, useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import {
  Page,
  Table,
  Button as SMButton,
  Pagination,
  Loader,
  PageTitleWithButton
} from '../../../components';
import { Button, Box } from '@mui/material';
import { TemplateModal } from './TemplateModal';
import { Add, Edit, Delete } from '@mui/icons-material';
import { IStartSheetTemplate, IStartSheetTemplateDetail } from '../../../models';
import {
  deleteStartSheetTemplate,
  getStartSheetTemplates,
  getStartSheetTemplateDetail
} from '../../../fetch';

interface ITemplatePage {
  type: string;
  title: string;
}

export const TemplatePage: FC<ITemplatePage> = ({ type, title }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingData, setLoadingData] = useState<boolean>(false);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [isLoadingDetail, setLoadingDetail] = useState<boolean>(false);
  const [isTemplateModalOpen, setTemplateModalOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<IStartSheetTemplateDetail>(null);
  const [data, setData] = useState<IStartSheetTemplate[]>([]);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);

  const fetchTemplates = async () => {
    try {
      setLoadingData(true);
      const res = await getStartSheetTemplates(type, {
        page: page + 1,
        perPage,
        sortBy: 'Name'
      });
      setRecordCount(res.totalRecordCount);
      setData(res.records);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        overrideWidth: 300,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IStartSheetTemplate } };
        }) => {
          return (
            <Box sx={{ textAlign: 'right' }}>
              <Button
                color="primary"
                startIcon={<Edit />}
                onClick={async () => {
                  setLoadingDetail(true);
                  try {
                    const res = await getStartSheetTemplateDetail(original.startSheetTemplateId);
                    setTemplateModalOpen(true);
                    setCurrentTemplate(res);
                  } catch (error) {
                    console.log(error);
                  } finally {
                    setLoadingDetail(false);
                  }
                }}
              >
                Edit
              </Button>
              <Button
                id="delete"
                color="error"
                startIcon={<Delete />}
                onClick={async () => {
                  const result = window.confirm(
                    'Are you sure you want to delete this sheet template?'
                  );
                  if (result) {
                    setDeleting(true);
                    try {
                      await deleteStartSheetTemplate(original.startSheetTemplateId);
                      enqueueSnackbar(`Sheet template deleted!`, {
                        variant: 'success'
                      });
                      fetchTemplates();
                    } catch (error) {
                      enqueueSnackbar(
                        'Error deleting sheet template, please refresh and try again.',
                        {
                          variant: 'error'
                        }
                      );
                      console.log(error);
                    } finally {
                      setDeleting(false);
                    }
                  }
                }}
              >
                Delete
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      {(isDeleting || isLoadingDetail) && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title={`${title} Start Sheet Templates`}>
        <PageTitleWithButton
          pageTitleBackProps={{
            backText: 'Back to Manage Start Templates',
            backUrl: '/admin/manage/start-templates',
            title: `${title} Start Sheet Templates`
          }}
          button={
            <SMButton
              id="create-new-template"
              startIcon={<Add />}
              onClick={() => setTemplateModalOpen(true)}
              color="primary"
              variant="contained"
            >
              Add New Template
            </SMButton>
          }
        />
        <Table columns={columns} data={data} isLoading={isLoadingData} hidePagination />
        {!isLoadingData && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Page>
      <TemplateModal
        currentTemplate={currentTemplate}
        open={isTemplateModalOpen}
        onClose={() => {
          setCurrentTemplate(null);
          setTemplateModalOpen(false);
        }}
        onSave={() => {
          fetchTemplates();
        }}
        type={type}
      />
    </>
  );
};
